import * as React from "react";
import { useField } from "formik";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import MUITextField from "@mui/material/TextField";
import { TextField } from "../../../components/forms/TextField";

interface StringProperty {
  type: "string";
  description?: string;
  enum?: string[];
}

interface StringArrayProperty {
  type: "array";
  description?: string;
}

interface IntegerProperty {
  type: "integer";
  description?: string;
  minimum?: number;
  maximum?: number;
}

type JsonSchemaProperty = StringProperty | StringArrayProperty | IntegerProperty;

interface SchemaFormProps {
  jsonSchema: Record<string, unknown>;
  namePrefix: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isNumber(value: unknown): value is number {
  return typeof value === "number";
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isString);
}

function parseProperty(value: unknown): JsonSchemaProperty | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  if (value.type === "string") {
    return {
      type: "string",
      description: isString(value.description) ? value.description : undefined,
      enum: isStringArray(value.enum) ? value.enum : undefined,
    };
  }
  if (value.type === "integer") {
    return {
      type: "integer",
      description: isString(value.description) ? value.description : undefined,
      minimum: isNumber(value.minimum) ? value.minimum : undefined,
      maximum: isNumber(value.maximum) ? value.maximum : undefined,
    };
  }
  if (value.type === "array" && isRecord(value.items) && value.items.type === "string") {
    return {type: "array", description: isString(value.description) ? value.description : undefined};
  }
  return undefined;
}

function parseProperties(jsonSchema: Record<string, unknown>): [string, JsonSchemaProperty | undefined][] {
  const properties = isRecord(jsonSchema.properties) ? jsonSchema.properties : {};
  return Object.entries(properties).map(([name, property]) => [name, parseProperty(property)]);
}

const leafName = (name: string) => name.split(".").pop() ?? name;

const StringArrayField = ({name, helperText}: {name: string, helperText?: string}) => {
  const [field, meta, helpers] = useField<string[]>(name);

  return (
    <MUIAutocomplete
      multiple
      freeSolo
      autoSelect
      options={[]}
      value={field.value ?? []}
      onChange={(_, value) => helpers.setValue(value)}
      renderInput={(params) =>
        <MUITextField {...params}
          name={name}
          label={leafName(name)}
          error={!!meta.error}
          helperText={meta.error ?? helperText}
        />}
    />
  );
};

const EnumField = ({name, options, helperText}: {name: string, options: string[], helperText?: string}) => (
  <TextField select name={name} label={leafName(name)} helperText={helperText}>
    {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
  </TextField>
);

const SchemaField = ({name, property}: {name: string, property: JsonSchemaProperty | undefined}) => {
  if (!property) {
    return <MUITextField disabled name={name} label={leafName(name)} helperText="unsupported setting type"/>;
  }
  if (property.type === "array") {
    return <StringArrayField name={name} helperText={property.description}/>;
  }
  if (property.type === "integer") {
    return (
      <TextField
        type="number"
        name={name}
        label={leafName(name)}
        helperText={property.description}
        inputProps={{min: property.minimum, max: property.maximum, step: 1}}
      />
    );
  }
  return property.enum
    ? <EnumField name={name} options={property.enum} helperText={property.description}/>
    : <TextField name={name} label={leafName(name)} helperText={property.description}/>;
};

export const SchemaForm = ({jsonSchema, namePrefix}: SchemaFormProps) => (
  <>
    {parseProperties(jsonSchema).map(([name, property]) =>
      <SchemaField key={name} name={`${namePrefix}.${name}`} property={property}/>)}
  </>
);
